import React, { useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import useDateRangeFilter from '../../hooks/useDateRangeFilter'
import Loading from '../../templates/UI/Loading/Loading'
import ReportTable from '../../templates/ReportTable/ReportTable'
import Dollars from '../../templates/Dollars/Dollars'
import { useAuth, useProtectedPage } from '../../context/AuthContext'
import DateRangeFilter from '../Reports/Filters/DateRangeFilter/DateRangeFilter'
import callAPI from '../../helpers/callAPI'
import CommissionsHistoryDetailsModal from '../Reports/CommissionsHistory/CommissionsHistoryDetailsModal'
import ReactTooltip from 'react-tooltip'

export default function CommissionReportNew({ showSystemMessage }) {
    const auth = useAuth()
    useProtectedPage(auth.permissions, 'commission_report')

    const [data, setData] = useState([])
    const [fetched, setFetched] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [password, setPassword] = useState('')
    const [snapshotFinalized, setSnapshotFinalized] = useState(false)
    const [saving, setSaving] = useState(false)
    const [showSaveConfirm, setShowSaveConfirm] = useState(false)
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
    const [selectedAdminId, setSelectedAdminId] = useState(null)
    const [selectedDate, setSelectedDate] = useState(null)

    const { selectedStartDate, selectedEndDate, quickDate, handleChangeStart, handleChangeEnd, handleQuickDate } = useDateRangeFilter()

    const handleReport = async () => {
        setFetched(true)
        setDataLoaded(false)
        setSnapshotFinalized(false)

        let response = await callAPI(`reports/commission/new?startDate=${selectedStartDate}&endDate=${selectedEndDate}`, 'POST', { password: password })

        if (response.success) {
            setData(response)
            setDataLoaded(true)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        let response = await callAPI(`reports/commission/new/save?startDate=${selectedStartDate}&endDate=${selectedEndDate}`, 'POST', {})
        setSaving(false)

        if (response.success) {
            setShowSaveConfirm(false)
            setSnapshotFinalized(true)
            showSystemMessage('success', 'Commission report snapshot saved.')
        }
    }

    const normalizeReportDate = (date) => {
        if (!date) {
            return null
        }

        const dateString = String(date)

        if (dateString.includes('T')) {
            return dateString.split('T')[0]
        }

        return dateString
    }

    const handleOpenDetailsModal = (adminId, date) => {
        setSelectedAdminId(adminId)
        setSelectedDate(normalizeReportDate(date))
        setIsDetailsModalOpen(true)
    }

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false)
    }

    const getReportOutput = () => {
        let columns = [
            {
                Header: 'Admin Id',
                accessor: 'admin.id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Name',
                accessor: 'admin.name',
                className: 'border-right',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Margin Target</div>,
                accessor: 'existing_target',
                className: 'text-right border-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Ratio</div>,
                accessor: 'existing_ratio',
                className: 'text-right border-right',
                Cell: ({ cell: { value } }) => {
                    return <>{value}%</>
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }} data-tip="This value has been incorporated into the commission calculation and is provided for informational purposes">AV Network Earnings</div>,
                accessor: 'av_network_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }} data-tip="This value has been incorporated into the commission calculation and is provided for informational purposes">AV Offset</div>,
                accessor: 'av_offset',
                className: 'text-right border-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Existing Margin</div>,
                accessor: 'existing_margin',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>New Margin</div>,
                accessor: 'new_margin',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Total Margin</div>,
                accessor: 'total_margin',
                className: 'text-right border-right',
                Cell: ({ cell: { value }, row: { original: row } }) => {
                    return <button type="button" className="btn btn-sm btn-link p-0 align-baseline" onClick={() => handleOpenDetailsModal(row.admin.id, selectedStartDate)}>
                                <Dollars value={value} />
                            </button>
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Existing Commission</div>,
                accessor: 'existing_commission',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Existing Comm Rate</div>,
                accessor: 'existing_comm_rate',
                className: 'text-right',
                Cell: ({ cell: { value }, row: { original: row } }) => {
                    return <>{Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}% (based on <Dollars value={row.mth_exist_biz_margin} />)</>
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>New Commission</div>,
                accessor: 'new_commission',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Total Commission</div>,
                accessor: 'total_commission',
                className: 'text-right border-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },            
        ]

        return (
            <React.Fragment>
                <ReactTooltip/>
                <ReportTable data={data.report} columns={columns} />
            </React.Fragment>
        )
    }

    return (
        <div>
            <React.Fragment>
                <CommissionsHistoryDetailsModal isOpen={isDetailsModalOpen} onClose={handleCloseDetailsModal} adminId={selectedAdminId} date={selectedDate || ''} mode="new" />
                <div className="page-header">
                    <span className="mb-h1">Protected Report</span>
                </div>
                <div className="card">                    
                    <div className="card-body">
                        <div className="d-flex align-items-center w-100">
                            <div className="d-flex align-items-center">
                                <div className="mr-4 d-flex align-items-center">
                                    <h1 className="mb-h1-dark">New Commission Report</h1>
                                </div>
                                <DateRangeFilter quickDate={quickDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate} handleChangeStart={handleChangeStart} handleChangeEnd={handleChangeEnd} handleQuickDate={handleQuickDate} />
                                <div className="mr-2">
                                    <input type="password" className="form-control form-control-sm" placeholder="Password" onChange={(e) => setPassword(e.target.value)}></input>
                                </div>
                                <div>
                                    <button onClick={handleReport} className="btn btn-sm btn-primary">
                                        Refresh Report
                                    </button>
                                </div>
                            </div>
                            {dataLoaded && !snapshotFinalized && data.report.length > 0 && (
                                <div className="ml-auto p-2 d-flex align-items-center">                                    
                                    <button onClick={() => setShowSaveConfirm(true)} className="btn btn-sm btn-success" disabled={saving}>
                                        Save Commission Report
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {fetched ? dataLoaded ? data.report.length === 0 ?<div className="p-3">Invalid password / no stats for the current dates.</div> : getReportOutput() : <Loading></Loading> : <></>}
                </div>
            </React.Fragment>

            <Modal show={showSaveConfirm} onHide={() => !saving && setShowSaveConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Save Commission Report History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">                            
                        <Button variant="primary" size="lg" onClick={handleSave} disabled={saving}>
                            Confirm
                        </Button>
                    </div>                    
                </Modal.Body>
            </Modal>
        </div>
    )
}
